/**
 * The desk's live socket (`/ws`) and its snapshot procedure. Server to client only after the
 * client's first message `{ type: 'auth', token }`. Cards mirror `CallCard` in `@lsc/domain`.
 */
import { oc } from '@orpc/contract';
import {} from '@lsc/domain';
import { z } from 'zod';
import { callerResolution } from './resolve.js';
import { teamMember } from './presence.js';
const cardState = z.enum(['ringing', 'connected', 'ended']);
export const callCardLeg = z.object({
    state: cardState,
    connected: z.boolean(),
    connectedAt: z.number().nullable(),
    endedAt: z.number().nullable(),
    transferred: z.boolean(),
    voicemail: z.boolean(),
    targetType: z.string().nullable(),
    targetId: z.string().nullable(),
});
export const callCard = z.object({
    rootCallId: z.string(),
    externalNumber: z.string().nullable(),
    state: cardState,
    endedHow: z.enum(['answered', 'missed', 'voicemail']).nullable(),
    answeredByDialpadUserId: z.string().nullable(),
    answeredByName: z.string().nullable(),
    answeredByProfileId: z.string().nullable(),
    startedAt: z.number().nullable(),
    connectedAt: z.number().nullable(),
    endedAt: z.number().nullable(),
    callCenterId: z.string().nullable(),
    ticket: z.object({ id: z.string(), number: z.string() }).nullable(),
    resolution: callerResolution.nullable(),
    resolutionError: z.boolean(),
    legs: z.record(z.string(), callCardLeg),
});
const callsStatus = z.enum(['ok', 'delayed']);
export const liveServerMessage = z.discriminatedUnion('type', [
    z.object({ type: z.literal('hello'), profileId: z.string(), cards: z.array(callCard), calls: callsStatus }),
    z.object({ type: z.literal('card'), card: callCard }),
    z.object({ type: z.literal('after_call'), card: callCard, expiresAt: z.number() }),
    z.object({ type: z.literal('status'), calls: callsStatus }),
    /** The whole team, right after `hello` and whenever departments may have changed. */
    z.object({ type: z.literal('team'), people: z.array(teamMember) }),
    /** One teammate's status changed. */
    z.object({ type: z.literal('presence'), member: teamMember }),
    /** A ticket changed somewhere (desk, Slack, the list mirror): refetch what shows it. `mentioned` is who should look at their notifications. */
    /** Who has this ticket open right now (Jeff, 2026-10-23); sent to everyone viewing it whenever it changes. */
    z.object({ type: z.literal('viewers'), ticketId: z.string(), people: z.array(z.object({ profileId: z.string(), name: z.string() })) }),
    z.object({ type: z.literal('ticket'), ticketId: z.string(), ticketNumber: z.string().nullable(), kind: z.string(), eventId: z.number().int().nullable(), mentioned: z.array(z.string()) }),
]);
export const liveClientAuth = z.object({ type: z.literal('auth'), token: z.string().min(1) });
/** After auth: the ticket this tab shows, or null when it leaves the ticket page. */
export const liveClientViewing = z.object({ type: z.literal('viewing'), ticketId: z.uuid().nullable() });
export const callsLiveOutput = z.object({ cards: z.array(callCard), calls: callsStatus });
export const callsLive = oc.route({ method: 'GET', path: '/calls/live', summary: 'Calls ringing or on now, the same cards the socket sends' }).output(callsLiveOutput);
/** One Support call in the log: every leg folded into one row. */
export const JOURNEY_STEP_KINDS = ['arrived', 'greeted', 'queued', 'rang', 'answered', 'hold', 'transferred', 'voicemail', 'missed', 'ended'];
export const journeyStep = z.object({
    kind: z.enum(JOURNEY_STEP_KINDS),
    at: z.string(),
    /** The rep's name for rang, answered, transferred, hold; null when nobody or not mapped. */
    who: z.string().nullable(),
    seconds: z.number().int().nullable(),
});
/** The call's path through Dialpad, from its legs: what the caller sat through and who it rang. */
export const callJourney = z.object({
    steps: z.array(journeyStep),
    waitSeconds: z.number().int().nullable(),
    holdSeconds: z.number().int(),
    talkSeconds: z.number().int().nullable(),
    totalSeconds: z.number().int().nullable(),
    partial: z.boolean(),
});
export const callLogRow = z.object({
    rootCallId: z.string(),
    startedAt: z.string().nullable(),
    externalNumber: z.string().nullable(),
    outcome: z.enum(['answered', 'missed', 'voicemail']),
    answeredByName: z.string().nullable(),
    /** The answering rep's desk profile, when the Dialpad user maps to one (so a ticket logged by someone else can go to them). */
    answeredByProfileId: z.string().nullable(),
    talkSeconds: z.number().int().nullable(),
    /** The ticket's account, else the one account the number matches; null when none or several. */
    account: z.object({ id: z.string(), label: z.string() }).nullable(),
    /** How many accounts the number matches, when no ticket says which. */
    accountMatches: z.number().int(),
    ticket: z.object({ id: z.string(), number: z.string() }).nullable(),
    recap: z.string().nullable(),
    /** Dialpad's transcript of the call is stored (redacted); `calls.transcript` reads it. */
    hasTranscript: z.boolean(),
    voicemailLink: z.string().nullable(),
    voicemailTranscript: z.string().nullable(),
    journey: callJourney,
});
export const callsListInput = z.object({
    outcome: z.enum(['all', 'missed']).default('all'),
    since: z.enum(['today', 'week', 'all']).default('week'),
    phone: z.string().trim().max(40).optional(),
    cursor: z.string().max(80).nullable().optional(),
    limit: z.number().int().min(1).max(100).default(50),
});
export const callsListOutput = z.object({ calls: z.array(callLogRow), total: z.number().int(), nextCursor: z.string().nullable() });
/** A ticket drafted from Dialpad's recap of one call (spec 2026-11-01-after-call-draft-design.md). */
export const callsDraftInput = z.object({ rootCallId: z.string().regex(/^\d{1,20}$/) });
export const callsDraftOutput = z.object({
    /** No recap yet and the call ended under 90 s ago: ask again in a few seconds. */
    waiting: z.boolean(),
    hasRecap: z.boolean(),
    subject: z.string().nullable(),
    notes: z.string().nullable(),
    resolvedOnCall: z.boolean(),
    /** A category the recap suggests (rules over the subject and topic sentence, else Dialpad's purpose); may be hidden in Settings, the desk checks. */
    category: z.string().nullable(),
    endedAt: z.string().nullable(),
});
/** A call's transcript as stored (redacted), every leg's lines in time order (spec 2026-11-02-call-transcripts-design.md). */
export const callsTranscriptInput = z.object({ rootCallId: z.string().regex(/^\d{1,20}$/) });
export const transcriptLineOut = z.object({ at: z.string().nullable(), speaker: z.enum(['rep', 'caller']), name: z.string().nullable(), text: z.string() });
export const callsTranscriptOutput = z.object({ lines: z.array(transcriptLineOut), fetched: z.boolean() });
/** The answered Support call a ticket for this account (or on this number) is probably about: within 20 minutes, no ticket yet (2026-11-02). */
export const callsRecentInput = z.object({ accountId: z.uuid().optional(), phone: z.string().trim().max(40).optional() });
export const callsRecentOutput = z.object({
    call: z
        .object({ rootCallId: z.string(), startedAt: z.string().nullable(), endedAt: z.string().nullable(), externalNumber: z.string().nullable(), answeredByName: z.string().nullable(), answeredByProfileId: z.string().nullable(), label: z.string().nullable() })
        .nullable(),
});
export const callsContract = {
    live: callsLive,
    recent: oc.route({ method: 'POST', path: '/calls/recent', summary: 'The answered call a new ticket for this account is probably about' }).input(callsRecentInput).output(callsRecentOutput),
    transcript: oc.route({ method: 'POST', path: '/calls/transcript', summary: "Dialpad's transcript of a call, redacted" }).input(callsTranscriptInput).output(callsTranscriptOutput),
    draft: oc.route({ method: 'POST', path: '/calls/draft', summary: "A ticket drafted from Dialpad's recap of a call" }).input(callsDraftInput).output(callsDraftOutput),
    list: oc.route({ method: 'POST', path: '/calls/list', summary: 'Support calls, newest first, one row per call' }).input(callsListInput).output(callsListOutput),
};
//# sourceMappingURL=live.js.map